<template>
  <div class="measurement-list">
    <!-- 查询条件：折叠后只显示标题栏 -->
    <el-card class="search-card" shadow="never">
      <div slot="header" class="search-header">
        <span class="search-title" @click="searchExpanded = !searchExpanded">
          查询条件
          <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </span>
      </div>
      <el-collapse-transition>
        <el-form v-show="searchExpanded" :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
          <el-form-item label="使用科室">
            <department-select v-model="queryForm.department" placeholder="全部科室" clearable style="width: 150px" />
          </el-form-item>
          <el-form-item label="设备名称">
            <el-input v-model="queryForm.device_name" placeholder="设备名称" clearable style="width: 170px" />
          </el-form-item>
          <!-- 出厂编号：模糊匹配（后端 factory_no_contains），输入片段即可，如 G300 -->
          <el-form-item label="出厂编号">
            <el-input
              v-model="queryForm.factory_no"
              placeholder="出厂编号（可输片段）"
              clearable
              style="width: 180px"
            />
          </el-form-item>
          <el-form-item label="有效期至">
            <el-date-picker
              v-model="queryForm.expire_date"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item label="检定日期">
            <el-date-picker
              v-model="queryForm.verify_date"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item label="检定单位">
            <el-select v-model="queryForm.verify_unit" placeholder="全部" clearable style="width: 220px">
              <el-option label="河北省计量监督检测研究院廊坊分院" value="河北省计量监督检测研究院廊坊分院" />
              <el-option label="香河县市场监督管理局检验所" value="香河县市场监督管理局检验所" />
              <el-option label="河北省计量监督检测研究院" value="河北省计量监督检测研究院" />
              <el-option label="河北中测计量检测有限公司" value="河北中测计量检测有限公司" />
              <el-option label="其他" value="其他" />
            </el-select>
          </el-form-item>
          <el-form-item label="检查结果">
            <el-select v-model="queryForm.conclusion" placeholder="全部" clearable style="width: 130px">
              <el-option label="合格" value="qualified" />
              <el-option label="不合格" value="unqualified" />
            </el-select>
          </el-form-item>
          <el-form-item label="器具分类">
            <el-select v-model="queryForm.category" placeholder="全部" clearable style="width: 130px">
              <el-option label="强检" value="compulsory" />
              <el-option label="非强检" value="non_compulsory" />
            </el-select>
          </el-form-item>
          <el-form-item class="search-buttons">
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-collapse-transition>
    </el-card>

    <!-- 表格 -->
    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <el-button type="success" icon="el-icon-plus" @click="openAdd">新增提醒</el-button>
        <el-button
          type="warning"
          icon="el-icon-download"
          :disabled="!selectedRows.length"
          @click="handleBatchExport"
        >批量导出</el-button>
        <el-button
          type="primary"
          icon="el-icon-folder"
          :disabled="!selectedRows.length"
          @click="handleExportCerts"
        >导出资质</el-button>
      </div>
      <!-- 到期汇总：登录进来第一眼就能看到几台过期 / 即将到期 -->
      <expiry-summary
        :expired="stats.expired"
        :soon="stats.soon"
        :total="stats.total"
        unit="台"
      />

      <el-table
        ref="mainTable"
        :data="list"
        row-key="id"
        border
        class="measurement-table"
        :row-class-name="rowClassName"
        @selection-change="handleSelectionChange"
        @sort-change="handleSortChange"
      >
        <el-table-column type="selection" width="55" align="center" :reserve-selection="true" />
        <el-table-column type="index" :index="mainIndex" label="序号" width="55" align="center" />
        <el-table-column prop="department" label="科室" width="110" />
        <el-table-column prop="device_name" label="设备名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="model" label="型号" width="120" show-overflow-tooltip />
        <el-table-column prop="factory_no" label="出厂编号" width="130" show-overflow-tooltip />
        <el-table-column prop="category_display" label="分类" width="90" align="center" sortable="custom" />
        <el-table-column prop="verify_date" label="检定日期" width="120" align="center" />
        <el-table-column prop="expire_date" label="有效期至" width="120" align="center" />
        <el-table-column prop="verify_cycle" label="检定周期" width="100" align="center" />
        <el-table-column prop="verify_unit" label="检定单位" min-width="180" show-overflow-tooltip />
        <el-table-column label="结论" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.conclusion === 'qualified' ? 'success' : 'danger'" size="medium">
              {{ scope.row.conclusion_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="PDF报告" width="90" align="center">
          <template slot-scope="scope">
            <a v-if="scope.row.pdf_path" :href="mediaUrl(scope.row.pdf_path)" target="_blank" class="pdf-link">查看</a>
            <span v-else class="pdf-none">未上传</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
        <el-table-column label="距到期" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.days_until_expire !== null"
              :type="scope.row.days_until_expire < 0 ? 'danger' : (scope.row.days_until_expire <= 30 ? 'warning' : 'info')"
              size="medium">
              <template v-if="scope.row.days_until_expire < 0">已过期</template>
              <template v-else-if="scope.row.days_until_expire <= 30">待报检</template>
              <template v-else>{{ scope.row.days_until_expire }}天</template>
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" align="center" fixed="right">
          <template slot-scope="scope">
            <el-tooltip :content="archiveTip(scope.row)" placement="top">
              <el-button type="text" icon="el-icon-time" class="archive-text" @click="openArchiveHistory(scope.row)" @mouseenter.native="onArchiveIconEnter(scope.row)" />
            </el-tooltip>
            <el-button type="text" class="archive-text" @click="openArchive(scope.row)">归档</el-button>
            <el-button type="text" @click="openEdit(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="currentPage"
        @size-change="handleSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <!-- 新增 / 编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px" :close-on-click-modal="false" @closed="resetForm">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px" class="measurement-form">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="使用科室" prop="department">
              <!-- 下拉选择同样触发 OCR 回填标记清除（backfilledKeys / onOcrFieldEdit 逻辑保留） -->
              <department-select v-model="form.department" placeholder="使用科室" :class="{ 'ocr-filled': backfilledKeys.includes('department') }" @change="onOcrFieldEdit('department')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备名称" prop="device_name">
              <el-input v-model="form.device_name" placeholder="设备名称" :class="{ 'ocr-filled': backfilledKeys.includes('device_name') }" @input="onOcrFieldEdit('device_name')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="规格型号">
              <el-input v-model="form.model" placeholder="规格型号" :class="{ 'ocr-filled': backfilledKeys.includes('model') }" @input="onOcrFieldEdit('model')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="出厂编号" prop="factory_no">
              <el-input
                ref="factoryNoInput"
                v-model="form.factory_no"
                placeholder="出厂编号"
                :class="{ 'ocr-filled': backfilledKeys.includes('factory_no') }"
                @blur="checkFactoryNo"
                @input="onOcrFieldEdit('factory_no')"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="器具分类">
              <el-select v-model="form.category" placeholder="请选择" style="width: 100%">
                <el-option label="强检" value="compulsory" />
                <el-option label="非强检" value="non_compulsory" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检测结论">
              <el-select v-model="form.conclusion" placeholder="请选择" style="width: 100%" :class="{ 'ocr-filled': backfilledKeys.includes('conclusion') }" @change="onOcrFieldEdit('conclusion')">
                <el-option label="合格" value="qualified" />
                <el-option label="不合格" value="unqualified" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检定日期" prop="verify_date">
              <el-date-picker v-model="form.verify_date" type="date" value-format="yyyy-MM-dd" placeholder="请选择检定日期" style="width: 100%" :class="{ 'ocr-filled': backfilledKeys.includes('verify_date') }" @change="onOcrFieldEdit('verify_date')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="form.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="自动计算 / 识别回填" style="width: 100%" :class="{ 'ocr-filled': backfilledKeys.includes('expire_date') }" @change="onOcrFieldEdit('expire_date')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检定周期">
              <el-input v-model="form.verify_cycle" placeholder="请输入月份，如 12" :class="{ 'ocr-filled': backfilledKeys.includes('verify_cycle') }" @input="onOcrFieldEdit('verify_cycle')">
                <template slot="suffix">月</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检定单位">
              <el-select v-model="form.verify_unit" placeholder="请选择" style="width: 100%" clearable :class="{ 'ocr-filled': backfilledKeys.includes('verify_unit') }" @change="onOcrFieldEdit('verify_unit')">
                <el-option label="河北省计量监督检测研究院廊坊分院" value="河北省计量监督检测研究院廊坊分院" />
                <el-option label="香河县市场监督管理局检验所" value="香河县市场监督管理局检验所" />
                <el-option label="河北省计量监督检测研究院" value="河北省计量监督检测研究院" />
                <el-option label="河北中测计量检测有限公司" value="河北中测计量检测有限公司" />
                <el-option label="其他" value="其他" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="备注" />
        </el-form-item>
        <el-form-item label="PDF报告">
          <div v-if="form.pdf_path && !pdfFile" class="pdf-existing">
            <a :href="mediaUrl(form.pdf_path)" target="_blank">查看当前文件</a>
          </div>
          <el-upload
            ref="upload"
            :auto-upload="false"
            :limit="1"
            accept=".pdf"
            drag
            :file-list="pdfFileList"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将 PDF 拖到此处，或<em>点击上传</em>（仅支持单个文件）</div>
          </el-upload>
          <!-- 识别报告：上传证书后点击，自动提取字段回填上方表单；不会自动保存 -->
          <div class="ocr-extract-row">
            <el-button
              type="primary"
              icon="el-icon-magic-stick"
              :loading="extracting"
              :disabled="!pdfFile"
              @click="handleExtract"
            >识别报告</el-button>
            <span class="ocr-tip">选择文件后点击识别，将回填上方表单；不会自动保存。</span>
          </div>
        </el-form-item>
        <!-- 识别回填确认区：回填后出现，勾选后方可保存；修改字段后自动取消勾选 -->
        <div v-if="ocrFilled" class="ocr-confirm-area">
          <div class="ocr-filled-tip">
            已回填 {{ backfilledKeys.length }} 个字段，黄色标注部分请对照原报告核对，缺失项请补填。
          </div>
          <el-checkbox v-model="ocrConfirmed" class="ocr-confirm-check">
            我已逐项核对识别结果，确认无误后保存（修改字段后需重新确认）
          </el-checkbox>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <!-- 归档（重新检定）弹窗 -->
    <el-dialog title="计量提醒归档（重新检定）" :visible.sync="archiveDialogVisible" width="720px" :close-on-click-modal="false" @closed="resetArchiveForm">
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="归档后将把旧计量证书移入归档文件夹（按 科室~设备名称~出厂编号~检定日期 命名），并以新的检定日期更新本记录、存入新证书。记录将保留在列表中。"
      />
      <el-form :model="archiveForm" :rules="archiveRules" ref="archiveForm" label-width="100px" class="measurement-form archive-form">
        <el-row :gutter="20" style="margin-top: 12px">
          <el-col :span="12">
            <el-form-item label="使用科室">
              <el-input :value="archiveForm.department" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备名称">
              <el-input :value="archiveForm.device_name" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="出厂编号">
              <el-input :value="archiveForm.factory_no" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检定周期">
              <el-input v-model="archiveForm.verify_cycle" placeholder="请输入月份，如 12">
                <template slot="suffix">月</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检定日期" prop="verify_date">
              <el-date-picker v-model="archiveForm.verify_date" type="date" value-format="yyyy-MM-dd" placeholder="请选择检定日期" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="archiveForm.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="自动计算" style="width: 100%" :disabled="true" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="新计量证书" prop="pdf_file">
          <el-upload
            ref="archiveUpload"
            :auto-upload="false"
            :limit="1"
            accept=".pdf"
            drag
            :file-list="archivePdfFileList"
            :on-change="handleArchiveFileChange"
            :on-remove="handleArchiveFileRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将新的计量证书 PDF 拖到此处，或<em>点击上传</em>（仅支持单个文件）</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="archiveDialogVisible = false">取消</el-button>
        <el-button type="warning" icon="el-icon-folder-checked" @click="submitArchive">确认归档</el-button>
      </span>
    </el-dialog>

    <!-- 归档记录查看弹窗 -->
    <el-dialog title="计量提醒归档记录" :visible.sync="archiveHistoryVisible" width="760px" :close-on-click-modal="false">
      <el-alert
        v-if="archiveRow"
        type="info"
        :closable="false"
        show-icon
        :title="`「${archiveRow.department || '-'} / ${archiveRow.device_name}」的历史归档文件（旧证书按归档批次存放于「归档」文件夹，按 科室~设备名称~出厂编号~检定日期 命名）`"
      />
      <el-table
        v-loading="archiveHistoryLoading"
        :data="archiveFiles"
        border
        size="small"
        empty-text="暂无归档记录"
        style="margin-top: 12px"
      >
        <el-table-column type="index" label="序号" width="55" align="center" />
        <el-table-column prop="year" label="年份" width="90" align="center" />
        <el-table-column prop="name" label="归档文件名" min-width="280" show-overflow-tooltip />
        <el-table-column label="大小" width="90" align="center">
          <template slot-scope="scope">{{ formatSize(scope.row.size) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <a :href="scope.row.url" target="_blank" class="pdf-link">查看</a>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="archiveHistoryVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 导出资质：年份选择弹窗 -->
    <el-dialog title="选择导出年份" :visible.sync="exportCertVisible" width="420px" :close-on-click-modal="false">
      <div class="export-year-tip">以下年份存在已选记录的计量证书（含当前证书与已归档证书），请勾选需要导出的年份（默认全选）：</div>
      <el-checkbox-group v-model="exportCertYears" class="export-year-group">
        <el-checkbox v-for="y in exportCertYearOptions" :key="y" :label="y">{{ y }} 年</el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="exportCertVisible = false">取消</el-button>
        <el-button type="primary" icon="el-icon-download" :loading="exporting" @click="confirmExportCerts">确认导出</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import api from '../api/measurementReminder'

import DepartmentSelect from './DepartmentSelect.vue'
import ExpirySummary from './ExpirySummary.vue'

export default {
  name: 'MeasurementReminderList',
  components: { DepartmentSelect, ExpirySummary },
  data() {
    return {
      // 顶部汇总：已过期 / 30 天内到期 / 合计（跟随当前筛选，后端 stats 接口给）
      stats: { expired: 0, soon: 0, total: 0 },
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      selectedRows: [],
      ordering: '',
      searchExpanded: false,
      queryForm: {
        department: '',
        device_name: '',
        factory_no: '',
        expire_date: ['', ''],
        verify_date: ['', ''],
        conclusion: '',
        category: '',
        verify_unit: '',
      },
      dialogVisible: false,
      dialogTitle: '新增计量提醒',
      editingId: null,
      pdfFile: null,
      pdfFileList: [],
      // 计量证书 OCR 识别回填状态
      extracting: false,   // 识别中（按钮 loading）
      ocrFilled: false,    // 是否已回填过字段（控制确认复选框与提示显隐）
      ocrConfirmed: false, // 用户是否已勾选「确认无误」
      backfilledKeys: [],  // 本次回填的非空字段名（用于黄底高亮）
      _applyingOcr: false, // 程序回填中标记，避免回填过程误触发确认重置
      // 出厂编号查重状态
      factoryNoExists: false,
      checkingFactoryNo: false,
      // 已弹过重复提示的值（同一个值反复失焦只提醒一次，别把用户烦死）
      _dupWarnedFor: '',
      // 查重请求序号：连续识别两次时丢弃过期响应，避免旧结论盖到新值上
      _factoryNoSeq: 0,
      // 保存后要高亮定位的那一行（id）
      highlightId: null,
      _highlightTimer: null,
      form: this.emptyForm(),
      rules: {
        device_name: [{ required: true, message: '请输入设备名称', trigger: 'blur' }],
        factory_no: [
          { required: true, message: '请输入出厂编号', trigger: 'blur' },
          // ⚠️ 查重不走字段校验：字段级报错会常驻在输入框下方，改了值/重新识别也不消失，
          // 还会把保存按钮卡死（必须刷新页面）。重复改用对话框提示，见 warnFactoryNoDuplicate。
        ],
        verify_date: [{ required: true, message: '请选择检定日期', trigger: 'change' }],
      },
      // 归档（重新检定）弹窗状态
      archiveDialogVisible: false,
      archiveId: null,
      archivePdfFile: null,
      archivePdfFileList: [],
      // 归档记录查看弹窗
      archiveHistoryVisible: false,
      archiveHistoryLoading: false,
      archiveFiles: [],
      archiveRow: null,
      archiveCounts: {},
      archiveForm: {
        department: '',
        device_name: '',
        factory_no: '',
        verify_date: '',
        expire_date: '',
        verify_cycle: '',
      },
      archiveRules: {
        verify_date: [{ required: true, message: '请选择检定日期', trigger: 'change' }],
      },
      // 导出资质弹窗状态
      exportCertVisible: false,
      exportCertYears: [],
      exportCertYearOptions: [],
      exporting: false,
    }
  },
  mounted() {
    this.fetchList()
  },
  watch: {
    // 检定日期或检定周期变化时，自动计算有效期至
    'form.verify_date': 'calcExpireDate',
    'form.verify_cycle': 'calcExpireDate',
    // 归档弹窗同样自动计算有效期至
    'archiveForm.verify_date': 'calcArchiveExpireDate',
    'archiveForm.verify_cycle': 'calcArchiveExpireDate',
    // 表单任意字段变化（深度监听）：若已回填且非程序回填，则取消「确认无误」勾选
    form: {
      handler() {
        if (this.ocrFilled && !this._applyingOcr) {
          this.ocrConfirmed = false
        }
      },
      deep: true,
    },
  },
  methods: {
    emptyForm() {
      return {
        department: '',
        device_name: '',
        model: '',
        factory_no: '',
        category: '',
        verify_date: '',
        expire_date: '',
        verify_cycle: '',
        verify_unit: '',
        conclusion: '',
        remark: '',
        pdf_path: '',
      }
    },
    mediaUrl(path) {
      if (!path) return ''
      return '/media/' + path
    },
    // 根据检定日期 + 检定周期（月）自动计算有效期至，处理月末溢出
    calcExpireDate() {
      // 证书识别回填的有效期至以证书为权威，手填检定日期/周期不覆盖；
      // 用户手动改动有效期至本身会移出 backfilledKeys，恢复自动计算
      if (this.backfilledKeys.includes('expire_date')) return
      const dateStr = this.form.verify_date
      const months = parseInt(this.form.verify_cycle, 10)
      if (!dateStr || isNaN(months) || months <= 0) return

      const parts = dateStr.split('-')
      if (parts.length !== 3) return

      const y = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      const d = parseInt(parts[2], 10)
      if (isNaN(y) || isNaN(m) || isNaN(d)) return

      const totalMonths = y * 12 + (m - 1) + months
      const newY = Math.floor(totalMonths / 12)
      const newM = (totalMonths % 12) + 1

      // 处理月末溢出：目标月份最后一天可能小于原日期
      const lastDay = new Date(newY, newM, 0).getDate()
      const newD = Math.min(d, lastDay)

      this.form.expire_date = `${newY}-${String(newM).padStart(2, '0')}-${String(newD).padStart(2, '0')}`
    },
    // 归档弹窗的自动计算（操作 archiveForm）
    calcArchiveExpireDate() {
      const dateStr = this.archiveForm.verify_date
      const months = parseInt(this.archiveForm.verify_cycle, 10)
      if (!dateStr || isNaN(months) || months <= 0) return

      const parts = dateStr.split('-')
      if (parts.length !== 3) return

      const y = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      const d = parseInt(parts[2], 10)
      if (isNaN(y) || isNaN(m) || isNaN(d)) return

      const totalMonths = y * 12 + (m - 1) + months
      const newY = Math.floor(totalMonths / 12)
      const newM = (totalMonths % 12) + 1

      const lastDay = new Date(newY, newM, 0).getDate()
      const newD = Math.min(d, lastDay)

      this.archiveForm.expire_date = `${newY}-${String(newM).padStart(2, '0')}-${String(newD).padStart(2, '0')}`
    },
    buildParams() {
      const p = { page: this.currentPage, page_size: this.pageSize }
      const q = this.queryForm
      if (q.department) p.department = q.department
      if (q.device_name) p.device_name = q.device_name
      // 出厂编号走模糊参数 factory_no_contains（factory_no 是精确查重专用，不能混用）
      if (q.factory_no && q.factory_no.trim()) p.factory_no_contains = q.factory_no.trim()
      // 有效期至 / 检定日期 改为时间范围查询
      if (q.expire_date && q.expire_date.length === 2 && q.expire_date[0] && q.expire_date[1]) {
        p.expire_date_start = q.expire_date[0]
        p.expire_date_end = q.expire_date[1]
      }
      if (q.verify_date && q.verify_date.length === 2 && q.verify_date[0] && q.verify_date[1]) {
        p.verify_date_start = q.verify_date[0]
        p.verify_date_end = q.verify_date[1]
      }
      if (q.conclusion) p.conclusion = q.conclusion
      if (q.category) p.category = q.category
      if (q.verify_unit) p.verify_unit = q.verify_unit
      if (this.ordering) p.ordering = this.ordering
      return p
    },
    // 顶部汇总：不带分页参数，只要筛选条件
    async fetchStats() {
      try {
        const p = this.buildParams()
        delete p.page
        delete p.page_size
        const res = await api.stats(p)
        const d = res.data || {}
        this.stats = {
          expired: d.expired || 0,
          soon: d.soon || 0,
          total: d.total || 0,
        }
      } catch (e) {
        // 汇总失败静默保持上次值，不打扰列表使用
      }
    },
    async fetchList() {
      try {
        const res = await api.list(this.buildParams())
        const data = res.data
        this.list = data.results || data
        this.total = data.count || (Array.isArray(data) ? data.length : 0)
        // 汇总与列表同源筛选，列表刷新后顺手刷新（失败不影响列表）
        this.fetchStats()
        // 跨页勾选由 row-key + reserve-selection 接管，不再手工清洗选中项
      } catch (e) {
        // 错误已由拦截器提示
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    // 切换每页条数：回到第 1 页，防止按旧的深页码请求越界
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchList()
    },
    handleReset() {
      this.resetQueryAndReload()
    },
    // 回到「默认视图」：清空全部查询条件 + 清排序 + 回第 1 页，然后刷新列表。
    // 抽成 async 方法是为了让调用方能 await 到列表真正回来，再做「定位到某一行」。
    async resetQueryAndReload() {
      this.queryForm = {
        department: '',
        device_name: '',
        factory_no: '',
        expire_date: ['', ''],
        verify_date: ['', ''],
        conclusion: '',
        category: '',
        verify_unit: '',
      }
      this.ordering = ''
      this.currentPage = 1
      await this.fetchList()
    },
    // 新增/编辑保存后把该行滚动到可视区并闪一下，避免用户"看到提示但找不到记录"
    async focusSavedRow(id) {
      if (!id) return
      this.highlightId = id
      if (this._highlightTimer) clearTimeout(this._highlightTimer)
      this._highlightTimer = setTimeout(() => {
        this.highlightId = null
      }, 2600)
      await this.$nextTick()
      const idx = (this.list || []).findIndex(r => r.id === id)
      if (idx < 0) return // 当前列表里没有（被筛选挡掉），不强行滚动
      const tableEl = this.$refs.mainTable && this.$refs.mainTable.$el
      if (!tableEl) return
      const trs = tableEl.querySelectorAll('.el-table__body-wrapper tbody > tr')
      const tr = trs[idx]
      if (tr && tr.scrollIntoView) tr.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    handleSortChange({ prop, order }) {
      if (prop === 'category_display' && order) {
        this.ordering = order === 'ascending' ? 'category' : '-category'
      } else {
        this.ordering = ''
      }
      this.fetchList()
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    // 到期行高亮：已过期=红、30 天内待报检=橙（与「距到期」标签口径一致）
    // ⚠️ 必须用 row-class-name + 样式，不能再用 row-style：
    // row-style 是把 background 写在 <tr> 上，而 App.vue 暖米白主题给每个
    // td.el-table__cell 都刷了不透明底色，把 tr 的底色整个盖住了 ——
    // 这就是「明明过期了却不变色」的真正原因（换成暖色调后才暴露）。
    rowClassName({ row }) {
      const cls = []
      const d = row.days_until_expire
      if (d != null && d < 0) cls.push('expired-row')
      else if (d != null && d <= 30) cls.push('soon-row')
      // 刚保存的那条额外闪一下（叠加在到期色之上，2.6s 后自动消失）
      if (this.highlightId && row.id === this.highlightId) cls.push('row-highlight')
      return cls.join(' ')
    },
    openAdd() {
      this.dialogTitle = '新增计量提醒'
      this.editingId = null
      this.form = this.emptyForm()
      this.pdfFile = null
      this.factoryNoExists = false
      this._dupWarnedFor = ''
      this._factoryNoSeq = 0
      this.extracting = false
      this.ocrFilled = false
      this.ocrConfirmed = false
      this.backfilledKeys = []
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = '编辑计量提醒'
      this.editingId = row.id
      this.form = {
        department: row.department || '',
        device_name: row.device_name || '',
        model: row.model || '',
        factory_no: row.factory_no || '',
        category: row.category || '',
        verify_date: row.verify_date || '',
        expire_date: row.expire_date || '',
        verify_cycle: row.verify_cycle || '',
        verify_unit: row.verify_unit || '',
        conclusion: row.conclusion || '',
        remark: row.remark || '',
        pdf_path: row.pdf_path || '',
      }
      this.pdfFile = null
      this.factoryNoExists = false
      this._dupWarnedFor = ''
      this._factoryNoSeq = 0
      this.extracting = false
      this.ocrFilled = false
      this.ocrConfirmed = false
      this.backfilledKeys = []
      this.dialogVisible = true
    },
    // 出厂编号失焦查重：命中重复时用**对话框**告知，不再往输入框下挂字段级红字。
    // 原来用 validateField 渲染错误，那条红字会一直留着（改了值、重新识别都不清），
    // 还会让 submitForm 的 validate 永远不通过 —— 表现为「必须刷新页面才能继续」。
    async checkFactoryNo() {
      const val = (this.form.factory_no || '').trim()
      this.factoryNoExists = false
      if (!val) return

      const seq = (this._factoryNoSeq || 0) + 1
      this._factoryNoSeq = seq
      this.checkingFactoryNo = true
      try {
        const res = await api.list({ factory_no: val, page: 1, page_size: 1 })
        if (seq !== this._factoryNoSeq) return // 已有更新的查重请求，丢弃本次结果
        const results = (res.data && res.data.results) || []
        // 编辑时排除当前记录自身，避免把自己当成重复
        const exists = results.some(r => r.factory_no === val && r.id !== this.editingId)
        this.factoryNoExists = exists
        if (exists) this.warnFactoryNoDuplicate(val)
      } catch (e) {
        // 查重失败（如后端未启动）不阻断填写
        if (seq === this._factoryNoSeq) this.factoryNoExists = false
      } finally {
        if (seq === this._factoryNoSeq) {
          this.checkingFactoryNo = false
          // 兜底清掉历史遗留的字段红字（老版本 validateField 留下的）
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.clearValidate('factory_no')
          })
        }
      }
    },
    // 重复提示对话框：确认=整页清空重来（可直接重新上传识别），取消=保留不动
    warnFactoryNoDuplicate(val) {
      if (this._dupWarnedFor === val) return
      this._dupWarnedFor = val
      this.$confirm(
        `出厂编号「${val}」已存在对应计量记录。同一台设备重新检定，请用列表里的「归档」功能录入新报告；如果只是录错了设备，点「清空重填」——会清空本页已填的全部字段和已上传的 PDF，之后可以直接重新上传识别，不用关弹窗也不用刷新页面。`,
        '出厂编号重复',
        {
          confirmButtonText: '清空重填',
          cancelButtonText: '保留不动',
          type: 'warning',
          distinguishCancelAndClose: true,
        }
      ).then(() => {
        if (this.editingId) {
          // 编辑已有记录时只能清这一个字段：整条清空后再保存会把空值写回原记录
          this.form.factory_no = ''
          this.factoryNoExists = false
          const idx = this.backfilledKeys.indexOf('factory_no')
          if (idx !== -1) this.backfilledKeys.splice(idx, 1)
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.clearValidate('factory_no')
          })
          return
        }
        // 新增：整个弹窗回到"刚打开"的状态 —— 表单字段、已上传 PDF、
        // 识别回填的黄底高亮/「已回填 N 个字段」提示/确认勾选全部清掉。
        // 之前只清了出厂编号，其余字段和文件都留着，用户还得刷新页面才能重新识别。
        this.resetForm()
      }).catch(() => {
        // 取消/关闭：什么也不做，仅关闭弹窗，不产生任何常驻提示
      })
    },
    // 出厂编号输入变化时重置查重标记与残留校验，避免旧状态挡住保存
    onFactoryNoInput() {
      this.factoryNoExists = false
      this._dupWarnedFor = ''
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate('factory_no')
      })
    },
    handleFileChange(file, fileList) {
      this.pdfFile = file.raw
      this.pdfFileList = fileList.slice(-1)
    },
    // 移除已选 PDF：清空上传状态，恢复为「未选择 / 显示当前文件」状态，无需关闭弹窗重开
    handleFileRemove() {
      this.pdfFile = null
      this.pdfFileList = []
    },
    // 识别报告：上传的计量证书 PDF 自动提取字段并回填上方表单（不会自动保存）
    async handleExtract() {
      if (!this.pdfFile) {
        this.$message.warning('请先上传 PDF 报告')
        return
      }
      this.extracting = true
      this._applyingOcr = true
      try {
        const fd = new FormData()
        fd.append('pdf_file', this.pdfFile)
        const res = await api.extractPdf(fd)
        const { data, is_scanned } = res.data
        // 仅合并非空字段；使用科室/器具分类/检定单位不识别（手填），
        // 有效期至来自证书明文，回填后手填检定周期不会覆盖它
        const fillKeys = [
          'device_name', 'model', 'factory_no',
          'verify_date', 'expire_date', 'verify_cycle', 'conclusion',
        ]
        const filled = []
        fillKeys.forEach(key => {
          const v = data[key]
          if (v !== undefined && v !== null && v !== '') {
            this.form[key] = v
            filled.push(key)
          }
        })
        this.backfilledKeys = filled
        this.ocrFilled = true
        this.ocrConfirmed = false
        // 回填不触发 blur，必须显式查重；命中时 validateField 会显示归档提示
        this.checkFactoryNo()
        const n = filled.length
        if (is_scanned) {
          this.$message.success(`已回填 ${n} 个字段，疑似扫描件，请重点核对`)
        } else {
          this.$message.success(`已回填 ${n} 个字段，黄色标注部分请对照原报告核对，缺失项请补填`)
        }
      } catch (e) {
        const msg = e.response?.data?.detail || '识别失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.extracting = false
        this._applyingOcr = false
      }
    },
    // 用户手动改动了某回填字段：移除黄底高亮、取消「确认无误」勾选
    onOcrFieldEdit(key) {
      if (this._applyingOcr) return // 程序回填过程不处理
      const idx = this.backfilledKeys.indexOf(key)
      if (idx !== -1) {
        this.backfilledKeys.splice(idx, 1)
      }
      if (this.ocrFilled) {
        this.ocrConfirmed = false
      }
      // 出厂编号改动时同步重置查重标记
      if (key === 'factory_no') {
        this.onFactoryNoInput()
      }
    },
    buildFormData() {
      const fd = new FormData()
      const fields = [
        'department', 'device_name', 'model', 'factory_no',
        'category', 'verify_date', 'expire_date', 'verify_cycle',
        'verify_unit', 'conclusion', 'remark',
      ]
      fields.forEach(f => {
        const v = this.form[f]
        if (v !== null && v !== undefined && v !== '') fd.append(f, v)
      })
      if (this.pdfFile) fd.append('pdf_file', this.pdfFile)
      return fd
    },
    async submitForm() {
      // 出厂编号重复时后端唯一性校验会拒绝保存；这里先弹对话框给出明确指引，
      // 避免用户点「保存」后只看到一个红色 toast、又不知道下一步该干什么。
      if (this.factoryNoExists) {
        this.$confirm(
          `出厂编号「${(this.form.factory_no || '').trim()}」已存在对应计量记录，不能重复新增。请改用列表「归档」功能录入新报告，或清空本字段重新填写。`,
          '出厂编号重复',
          { confirmButtonText: '知道了', showCancelButton: false, type: 'warning' }
        ).catch(() => {})
        return
      }
      this.$refs.form.validate(async valid => {
        if (!valid) return
        // OCR 回填后必须先勾选「确认无误」方可保存
        if (this.ocrFilled && !this.ocrConfirmed) {
          this.$message.warning('请先勾选「我已逐项核对识别结果，确认无误后保存」')
          return
        }
        try {
          const fd = this.buildFormData()
          let savedId = this.editingId
          if (this.editingId) {
            await api.update(this.editingId, fd)
            this.$message.success('保存成功')
            // 编辑后仍可能被当前筛选挡住（比如改了科室），刷新一次即可
            await this.fetchList()
          } else {
            const res = await api.create(fd)
            savedId = (res && res.data && res.data.id) || null
            this.$message.success('新增成功')
            // 新增后一律回到默认视图（清筛选/清排序/第 1 页），否则带着筛选刷新时
            // 新记录很可能不在当前页，用户会以为「没新增成功」而反复重录
            await this.resetQueryAndReload()
          }
          this.dialogVisible = false
          this.focusSavedRow(savedId)
        } catch (e) {
          const data = e.response && e.response.data
          let msg = '保存失败，请检查后端服务'
          if (data) {
            if (typeof data === 'string') msg = data
            else if (data.factory_no) msg = data.factory_no[0]
            else if (data.detail) msg = data.detail
            else if (data.non_field_errors) msg = data.non_field_errors[0]
          }
          this.$message.error(msg)
        }
      })
    },
    // 主列表序号：跨页连续编号（第 2 页从 11 开始，不重新起 1）
    mainIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    // 拉取与当前筛选/排序一致的全量列表，建立 id→序号 映射（序号=在全量结果中的位置，与页面显示一致）
    // 后端 max_page_size=100，超过 100 条时循环翻页补齐
    async fetchSeqMap() {
      const map = {}
      try {
        const pageSize = 100
        let page = 1
        let total = Infinity
        while ((page - 1) * pageSize < total) {
          const params = { ...this.buildParams(), page, page_size: pageSize }
          const res = await api.list(params)
          const data = res.data
          const rows = data.results || data
          rows.forEach((row, i) => { map[row.id] = (page - 1) * pageSize + i + 1 })
          total = data.count || rows.length
          if (!rows.length) break
          page++
        }
      } catch (e) {
        // 拉取失败返回空映射，序号留空但不阻塞导出
      }
      return map
    },
    async handleBatchExport() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在列表中至少选择一条需要导出的记录')
        return
      }
      const seqMap = await this.fetchSeqMap()

      const headers = {
        seq: '序号',
        department: '科室',
        device_name: '设备名称',
        model: '型号',
        factory_no: '出厂编号',
        category_display: '分类',
        verify_date: '检定日期',
        expire_date: '有效期至',
        verify_cycle: '检定周期',
        verify_unit: '检定单位',
        conclusion_display: '结论',
        remark: '备注',
        pdf_path: 'PDF路径',
        days_until_expire: '距到期天数',
        created_at: '创建时间',
        updated_at: '更新时间',
      }

      const exportData = this.selectedRows.map(row => {
        const item = {}
        Object.keys(headers).forEach(key => {
          let value = row[key]
          if (key === 'seq') value = seqMap[row.id] || ''
          item[headers[key]] = value !== undefined && value !== null ? value : ''
        })
        return item
      })

      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '计量提醒')

      const count = this.selectedRows.length
      const firstName = this.selectedRows[0].device_name || '导出'
      const fileName = count === 1
        ? `计量提醒_${firstName}_${this.formatDateForFile(new Date())}.xlsx`
        : `计量提醒_批量${count}条_${this.formatDateForFile(new Date())}.xlsx`
      XLSX.writeFile(wb, fileName)
      this.$message.success(`已成功导出 ${count} 条记录`)
    },
    formatDateForFile(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}${m}${day}_${h}${min}${s}`
    },
    // 导出资质：向后端查询所选记录可导出证书的年份（含当前证书 + 已归档证书），再弹出年份多选框
    async handleExportCerts() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在列表中至少选择一条需要导出的记录')
        return
      }
      const ids = this.selectedRows.map(r => r.id)
      try {
        const res = await api.availableYears(ids)
        const years = (res.data && res.data.years) || []
        if (!years.length) {
          this.$message.warning('所选记录均没有可导出（当前或已归档）的计量证书，暂无法确定导出年份')
          return
        }
        this.exportCertYearOptions = years
        this.exportCertYears = [...years] // 默认全选
        this.exportCertVisible = true
      } catch (e) {
        const msg = e.response?.data?.detail || '获取可导出年份失败，请检查后端服务'
        this.$message.error(msg)
      }
    },
    async confirmExportCerts() {
      if (!this.exportCertYears.length) {
        this.$message.warning('请至少选择一个导出年份')
        return
      }
      this.exporting = true
      try {
        const ids = this.selectedRows.map(r => r.id)
        const res = await api.exportFiles({ ids, years: this.exportCertYears })
        this.saveBlob(res.data, `计量证书_导出_${this.formatDateForFile(new Date())}.zip`)
        this.$message.success(`已导出 ${this.exportCertYears.join('/')} 年证书`)
        this.exportCertVisible = false
      } catch (e) {
        let msg = '导出失败，请检查后端服务'
        const resp = e.response
        if (resp && (resp.status === 400 || resp.status === 403)) {
          // 400/403 时后端返回 JSON（被 responseType:blob 包裹为 Blob），解析出来提示
          try {
            const text = await resp.data.text()
            const j = JSON.parse(text)
            if (j.detail) msg = j.detail
          } catch (_) { /* 解析失败则用默认提示 */ }
        } else if (resp && resp.status === 401) {
          msg = '未登录或登录已过期，请重新登录'
        }
        this.$message.error(msg)
      } finally {
        this.exporting = false
      }
    },
    // 把后端返回的 ZIP blob 触发浏览器下载
    saveBlob(buffer, fileName) {
      const blob = (buffer && buffer instanceof Blob) ? buffer : new Blob([buffer])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    // 打开归档（重新检定）弹窗，预填当前记录信息
    openArchive(row) {
      this.archiveId = row.id
      this.archiveForm = {
        department: row.department || '',
        device_name: row.device_name || '',
        factory_no: row.factory_no || '',
        verify_date: row.verify_date || '',
        expire_date: row.expire_date || '',
        verify_cycle: row.verify_cycle || '',
      }
      this.archivePdfFile = null
      this.archiveDialogVisible = true
      // 预填后按当前周期重算有效期至，保持与表单一致
      this.$nextTick(() => this.calcArchiveExpireDate())
    },
    handleArchiveFileChange(file, fileList) {
      this.archivePdfFile = file.raw
      this.archivePdfFileList = fileList.slice(-1)
    },
    handleArchiveFileRemove() {
      this.archivePdfFile = null
      this.archivePdfFileList = []
    },
    async submitArchive() {
      this.$refs.archiveForm.validate(async valid => {
        if (!valid) return
        if (!this.archivePdfFile) {
          this.$message.warning('请上传新的计量证书文件')
          return
        }
        try {
          const fd = new FormData()
          fd.append('verify_date', this.archiveForm.verify_date)
          if (this.archiveForm.expire_date) fd.append('expire_date', this.archiveForm.expire_date)
          if (this.archiveForm.verify_cycle !== '' && this.archiveForm.verify_cycle != null) {
            fd.append('verify_cycle', this.archiveForm.verify_cycle)
          }
          fd.append('pdf_file', this.archivePdfFile)
          await api.archive(this.archiveId, fd)
          this.$message.success('归档成功')
          this.archiveDialogVisible = false
          this.$set(this.archiveCounts, this.archiveId, undefined)
          this.fetchList()
        } catch (e) {
          const data = e.response && e.response.data
          let msg = '归档失败，请检查后端服务'
          if (data) {
            if (data.detail) msg = data.detail
            else if (data.verify_date) msg = data.verify_date[0]
          }
          this.$message.error(msg)
        }
      })
    },
    // 查看归档记录：拉取本条记录在硬盘「归档」文件夹中的历史证书文件
    async openArchiveHistory(row) {
      this.archiveRow = row
      this.archiveHistoryVisible = true
      this.archiveHistoryLoading = true
      this.archiveFiles = []
      try {
        const res = await api.archiveFiles(row.id)
        this.archiveFiles = (res.data && res.data.results) || []
        this.$set(this.archiveCounts, row.id, this.archiveFiles.length)
      } catch (e) {
        this.$message.error('获取归档记录失败，请检查后端服务')
      } finally {
        this.archiveHistoryLoading = false
      }
    },
    formatSize(n) {
      if (n === null || n === undefined) return '-'
      if (n < 1024) return n + ' B'
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
      return (n / 1024 / 1024).toFixed(1) + ' MB'
    },
    // 归档图标悬浮提示文案：读缓存的条数；未加载过则提示“查看归档记录”
    archiveTip(row) {
      const c = this.archiveCounts[row.id]
      if (c === undefined) return '查看归档记录'
      if (c === 0) return '暂无归档记录'
      if (c === null) return '加载中…'
      return `${c} 条归档记录`
    },
    // 首次悬浮图标时懒加载该记录的归档条数（之后缓存，避免重复请求）
    onArchiveIconEnter(row) {
      if (this.archiveCounts[row.id] !== undefined) return
      this.$set(this.archiveCounts, row.id, null)
      api.archiveFiles(row.id).then(res => {
        const cnt = (res.data && res.data.count) != null ? res.data.count : 0
        this.$set(this.archiveCounts, row.id, cnt)
      }).catch(() => {
        this.$set(this.archiveCounts, row.id, 0)
      })
    },
    async handleDelete(row) {
      try {
        await this.$confirm(
          `确认删除「${row.device_name}」的计量提醒记录吗？删除后其计量证书将移入「删除」文件夹，列表不再显示该记录（数据库保留为已删除状态，可按需恢复）。`,
          '删除确认',
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      try {
        await api.delete(row.id)
        this.$message.success('删除成功')
        // 跨页保留勾选模式下，清空整个勾选（避免已删行残留在勾选中）
        this.$refs.mainTable.clearSelection()
        this.fetchList()
      } catch (e) {
        this.$message.error('删除失败，请检查后端服务')
      }
    },
    resetArchiveForm() {
      this.archiveForm = {
        department: '',
        device_name: '',
        factory_no: '',
        verify_date: '',
        expire_date: '',
        verify_cycle: '',
      }
      this.archivePdfFile = null
      this.archivePdfFileList = []
      if (this.$refs.archiveUpload) this.$refs.archiveUpload.clearFiles()
    },
    resetForm() {
      this.form = this.emptyForm()
      this.pdfFile = null
      this.pdfFileList = []
      this.factoryNoExists = false
      this._dupWarnedFor = ''
      this._factoryNoSeq = 0
      this.extracting = false
      this.ocrFilled = false
      this.ocrConfirmed = false
      this.backfilledKeys = []
      if (this.$refs.upload) this.$refs.upload.clearFiles()
      // 表单值都清空了，必须同时清校验状态，否则"请输入出厂编号"之类的红字会残留
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
  },
}
</script>

<style scoped>
.measurement-list {
  padding: 16px;
}
.search-card /deep/ .el-card__body {
  padding: 0;
}
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.search-title {
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
}
.search-title:hover {
  color: #409eff;
}
/* 查询区缩小一号：label 13px，间距收紧（控件尺寸由 size="small" 接管） */
.search-form /deep/ .el-form-item__label {
  font-size: 13px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px;
}
.search-form .el-form-item {
  margin-bottom: 8px;
}
.search-form .range-sep {
  margin: 0 6px;
  color: #909399;
}
.search-buttons {
  margin-left: auto;
}
.table-card {
  margin-top: 16px;
}
.table-toolbar {
  margin-bottom: 12px;
  text-align: left;
}
.measurement-table /deep/ .el-table__body tr:hover > td {
  background-color: #ecf5ff !important;
}
/* 到期高亮（配色与「报告提醒」统一，且必须在暖米白底 #f6f1e7 上看得出来）
   旧实现用 row-style 把颜色写在 tr 上，被 td 的全局底色盖掉，等于没生效。 */
.measurement-table /deep/ .el-table__body tr.expired-row > td {
  background-color: #fad7d5 !important;
}
.measurement-table /deep/ .el-table__body tr.expired-row > td:first-child {
  border-left: 3px solid #f56c6c;
}
.measurement-table /deep/ .el-table__body tr.soon-row > td {
  background-color: #fbe9c9 !important;
}
.measurement-table /deep/ .el-table__body tr.soon-row > td:first-child {
  border-left: 3px solid #e6a23c;
}
.measurement-table /deep/ .el-table__body tr.expired-row:hover > td {
  background-color: #f6c6c3 !important;
}
.measurement-table /deep/ .el-table__body tr.soon-row:hover > td {
  background-color: #f7dfb2 !important;
}
/* 新增/编辑保存后定位高亮：
   用 inset box-shadow 叠一层半透明绿，而不是直接改 background-color ——
   这样能叠在「到期红/橙」之上，不会把状态色冲掉，类被移除时还有淡出过渡。 */
.measurement-table /deep/ .el-table__body tr.row-highlight > td {
  box-shadow: inset 0 0 0 9999px rgba(103, 194, 58, 0.22);
  transition: box-shadow 0.4s ease;
}
.measurement-table /deep/ .el-table__body tr > td {
  transition: background-color 0.2s;
}
/* 表格所有单元格内容与表头居中 */
.measurement-table /deep/ th,
.measurement-table /deep/ td {
  text-align: center !important;
}
.measurement-table /deep/ th .cell {
  text-align: center !important;
}
.measurement-table /deep/ td .cell {
  text-align: center !important;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.pdf-link {
  color: #409eff;
}
.pdf-none {
  color: #c0c4cc;
}
.archive-text {
  color: #e6a23c;
}
.danger-text {
  color: #f56c6c;
}
/* 弹窗输入框蓝色描边 + 字号加大，与全局风格一致 */
.measurement-form /deep/ .el-input__inner {
  border-color: #409eff;
  font-size: 15px;
}
.measurement-form /deep/ .el-textarea__inner {
  border-color: #409eff;
  font-size: 15px;
}
.pdf-uploader {
  width: 100%;
}
/* OCR 识别回填：黄底高亮被回填的输入框（el-input / el-select / el-date-picker 通用） */
.measurement-form /deep/ .ocr-filled .el-input__inner,
.measurement-form /deep/ .ocr-filled .el-textarea__inner {
  background: #fdf6ec;
}
/* 识别报告按钮行 + 灰色说明 */
.ocr-extract-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.ocr-tip {
  color: #909399;
  font-size: 12px;
}
/* 回填确认区文案 */
.ocr-confirm-area {
  margin: 4px 0 0 100px;
}
.ocr-filled-tip {
  color: #b88230;
  font-size: 13px;
  margin-bottom: 6px;
}
.ocr-confirm-check {
  font-size: 13px;
}
/* 导出资质年份选择弹窗 */
.export-year-tip {
  color: #606266;
  font-size: 14px;
  margin-bottom: 14px;
  line-height: 1.6;
}
.export-year-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 6px;
}
.export-year-group .el-checkbox {
  margin-right: 0;
}
</style>
